import React, { useState, useEffect, useMemo } from "react";
import { AgGridReact } from 'ag-grid-react';
import { Box, Paper, CircularProgress, Typography, Breadcrumbs, Link, Button, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';

const FilterTable = ({
  columnDefs,
  rowData,
  onRowDoubleClicked,
  defaultColDef: customDefaultColDef,
  isLoading,
  onGridReady,
  paginationPageSize = 1000,
  paginationPageSizeSelector = [50, 100, 500, 1000, 5000],
  rowHeight = 30,
  headerHeight = 30,
  floatingFiltersHeight = 28,
  suppressRowClickSelection = true,
  enableCellTextSelection = true,
  ensureDomOrder = true,
  suppressMenuHide = true,
  enableRangeSelection = true,
  sideBar = false,
  suppressHorizontalScroll = false,
  rowSelection = 'single',
  customStyles = '',
  noRowsMessage = 'No data found',
  loadingMessage = 'Loading data...',
  // Breadcrumb props
  onHomeClick = () => { },
  onParentClick = () => { },
  breadcrumbParentLabel = 'Ticketing',
  breadcrumbCurrentLabel = 'Warden/Vendor Allocation Master',
  // New button props
  onAddClick = () => { },
  hasAddPermission = false,
  onCellClicked,
  getRowStyle,
  showCalendarButton = false,
  onCalendarClick = () => { },
  calendarButtonTooltip = 'Open Calendar',
  hasCalendarPermission = true,
}) => {
  const gridStyles = `
    .ag-theme-alpine {
      --ag-border-color: #c8c8c8;
      --ag-header-background-color: #f8f9fa;
      --ag-odd-row-background-color: #fcfcfc;
      --ag-border-radius: 6px;
    }
    
    .ag-theme-alpine .ag-cell {
      border-right: 1px solid #e0e0e0;
      border-bottom: 1px solid #e0e0e0;
      display: flex;
      align-items: center;
      overflow: hidden; 
      text-overflow: ellipsis;
    }
    
    .ag-theme-alpine .ag-header-cell {
      border-right: 1px solid #c8c8c8;
      border-bottom: 1px solid #c8c8c8;
      font-weight: 600;
    }
    
    .ag-theme-alpine .ag-floating-filter-input {
      border-radius: 4px;
    }
    
    .ag-theme-alpine .ag-floating-filter-input:focus {
      outline: none;
      border-color: #3f51b5;
      box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.2);
    }
    
    .ag-theme-alpine .ag-header-cell::after {
      display: none;
    }
    
    .ag-theme-alpine .ag-ltr .ag-cell-first-right-pinned {
      border-left: 1px solid #c8c8c8;
    }
    
    .ag-floating-filter-button {
      margin-left: 0px !important;
    }
    
    .ag-header-cell {
      padding-left: 2px;
      padding-right: 0px;
    }
    
    .truncated-cell {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
    
    .status-chip {
      height: 20px;
      font-size: 0.75rem;
      font-weight: 600;
    }
    
    .ag-input-wrapper:before {
      background-color: white;
      display: none;
    }
    
    .ag-theme-alpine .ag-floating-filter input[type="date"] {
      padding-left: 0 !important;
    }
    
    :where(.ag-ltr) .ag-header-cell-resize {
      right: -6px !important;
    }

    @media (max-width: 600px) {
      .ag-theme-alpine .ag-paging-panel {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 10px 2px !important;
        box-sizing: border-box;
        text-align: center;
        width: 100%;
        min-height: 120px;
        gap: 10px;
      }

      .ag-theme-alpine .ag-paging-row-summary-panel,
      .ag-theme-alpine .ag-paging-page-size {
        display: flex !important;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        flex: 1;
        gap: 16px;
        white-space: nowrap;
        margin: 0;
      }

      .ag-theme-alpine .ag-paging-panel > .ag-paging-row-summary-panel,
      .ag-theme-alpine .ag-paging-panel > .ag-paging-page-size {
        flex: none;
      }

      .ag-theme-alpine .ag-paging-row-summary-panel::after {
        content: '';
        flex-grow: 1;
      }

      .ag-theme-alpine .ag-paging-button-panel {
        display: flex !important;
        justify-content: center !important;
        flex-wrap: wrap;
        width: 100%;
        margin-top: 2px;
      }

      .ag-theme-alpine .ag-paging-page-size select,
      .ag-theme-alpine .ag-paging-page-size input {
        max-width: 70px !important;
        font-size: 14px !important;
        padding: 4px;
      }
    }
    
    ${customStyles}
  `;

  const addButtonStyles = {
    background: hasAddPermission ? "#635bff" : "rgba(0, 0, 0, 0.12)",
    color: hasAddPermission ? "white" : "rgba(0, 0, 0, 0.26)",
    borderRadius: "50px",
    padding: "8px 20px",
    boxShadow: hasAddPermission ? "0px 6px 15px rgba(0, 0, 0, 0.3)" : "none",
    transition: "background 0.3s ease",
    "&:hover": {
      background: hasAddPermission ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
    },
    "&:disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.26)"
    }
  };

  const rowSelectionConfig = rowSelection === 'single'
    ? { mode: 'singleRow', enableClickSelection: !suppressRowClickSelection }
    : rowSelection === 'multiple'
      ? { mode: 'multiRow', enableClickSelection: !suppressRowClickSelection }
      : undefined;

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = gridStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [customStyles]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    filterParams: {
      suppressAndOrCondition: true,
    },
    cellStyle: {
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center'
    },
    headerClass: 'ag-header-cell',
    suppressSizeToFit: true,
    ...customDefaultColDef
  }), [customDefaultColDef]);

  const calendarButtonStyles = {
    background: hasCalendarPermission ? "#4caf50" : "rgba(0, 0, 0, 0.12)",
    color: hasCalendarPermission ? "white" : "rgba(0, 0, 0, 0.26)",
    borderRadius: "50px",
    padding: "8px 20px",
    marginRight: "10px",
    boxShadow: hasCalendarPermission ? "0px 4px 10px rgba(0, 0, 0, 0.2)" : "none",
    transition: "background 0.3s ease",
    "&:hover": {
      background: hasCalendarPermission ? "#3d8b40" : "rgba(0, 0, 0, 0.12)"
    },
    "&:disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.26)"
    }
  };

  const normalizedRowSelection = typeof rowSelection === "string"
    ? { mode: rowSelection === "single" ? "singleRow" : "multiRow", enableClickSelection: !suppressRowClickSelection }
    : rowSelection;
  return (
    <Box sx={{
      width: { xs: "calc(100vw - 60px)", sm: "100%", md: "100%" },
      maxWidth: { xs: "calc(100vw - 60px)", sm: "100%", md: "100%" },
      height: '97%',
      display: 'flex',
      flexDirection: 'column',
      p: 2,
      background: 'rgb(249, 249, 249)'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        rowGap: 2,
        background: 'rgb(255, 255, 255)',
        padding: '10px 12px 0 12px',
        borderRadius: '5px',
        boxShadow: 'rgb(0 0 0 / 8%)'
      }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            fontSize: { xs: "18px", sm: "16px" },
            display: "flex",
            justifyContent: 'center',
            flexDirection: { xs: "column", sm: "column", md: "row" },
            alignItems: { xs: "center", sm: "flex-start" }
          }}
        >
          <Link
            underline="hover"
            color="#635bff"
            sx={{
              cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center",
            }}
            onClick={onHomeClick}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Box component="span">Home</Box>
          </Link>
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={onParentClick}
          >
            {breadcrumbParentLabel}
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: "500", fontSize: { xs: "18px", sm: "16px" } }}>
            {breadcrumbCurrentLabel}
          </Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {showCalendarButton && (
            <Tooltip
              title={hasCalendarPermission ? calendarButtonTooltip : 'You have no permission to access calendar'}
              placement="top"
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={onCalendarClick}
                  size="small"
                  disabled={!hasCalendarPermission}
                  sx={calendarButtonStyles}
                // startIcon={<CalendarTodayIcon />}
                >
                  Calendar
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={hasAddPermission ? 'Add New Record' : 'You have no permission to add record'} placement="top" arrow>
            <span>
              <Button
                variant="contained"
                onClick={onAddClick}
                size="small"
                disabled={!hasAddPermission}
                sx={addButtonStyles}
              >
                <AddIcon /> New
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Paper
        elevation={2}
        sx={{
          height: 'auto',
          minHeight: 'calc(100vh - 250px)',
          width: '100%',
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          className="ag-theme-alpine"
          style={{
            height: '100%',
            width: '100%',
            overflowX: 'auto'
          }}
        >
          {isLoading ? (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}>
              <CircularProgress size='3rem' sx={{ color: '#635bff' }} />
              <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                {loadingMessage}
              </Typography>
            </Box>
          ) : (
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rowData}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowDoubleClicked={onRowDoubleClicked}
              pagination={true}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              enableCellTextSelection={enableCellTextSelection}
              ensureDomOrder={ensureDomOrder}
              rowHeight={rowHeight}
              headerHeight={headerHeight}
              floatingFiltersHeight={floatingFiltersHeight}
              overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${loadingMessage}</span>`}
              overlayNoRowsTemplate={`<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">${noRowsMessage}</span>`}
              suppressMenuHide={suppressMenuHide}
              sideBar={sideBar}
              suppressHorizontalScroll={suppressHorizontalScroll}
              onCellClicked={onCellClicked}
              getRowStyle={getRowStyle}
              rowSelection={rowSelection}
              suppressRowClickSelection={true}
              suppressCellFocus={true}
            />
          )}
        </Box>
      </Paper>
      <Typography variant="body2" sx={{
        fontWeight: 'bold', position: 'absolute', bottom: '55px',
        display: { xs: 'none', sm: 'none', md: 'block' },
        marginLeft: '5px'
      }}>
        Total Records: {rowData.length}
      </Typography>
    </Box>
  );
};

export default FilterTable;